// One saved calculation result (Lane 1344, Phase 3 slice 3B), shown in a scenario's
// Details. Every figure comes from the server's saved result. The "Status now" and
// award checklist sections were removed in Lane 1395 (Ryan); the award review checks
// what an award needs.
import React, { useEffect, useState } from 'react';
import { formatAmount, formatUnitPrice, formatUnits } from './sourcingApi';
import './goal.css';
// formatUnitPrice is also used by the recommendation's target prices.
import { HistoryMark } from './SupplierHistory';
import { RESULT_STATUS_TEXT, plural, ruleText, supplierName } from './scenarioText';

const WHY_TEXT = {
  set_by_you: 'Your what-if', cheapest: 'Lowest price allowed', kept_as_today: 'Cheaper to keep as today',
  held: 'A rule or limit (minimum order, supplier count, preference)', no_option: 'No offer can take it',
};

function coverageLine(t) {
  const cov = t.coverage || {};
  const offered = cov.offers ? cov.offers.lines : 0;
  const kept = cov.no_change ? cov.no_change.lines : 0;
  const missing = (cov.unallocatable ? cov.unallocatable.lines : 0);
  const notCalc = t.not_calculated ? t.not_calculated.lines : 0;
  const parts = [`${offered} ${offered === 1 ? 'line' : 'lines'} to offers`];
  if (kept) parts.push(`${kept} kept as today`);
  if (missing) parts.push(`${missing} with no option (value today ${cov.unallocatable.value_today ? formatAmount(cov.unallocatable.value_today) : 'unknown'})`);
  if (notCalc) parts.push(`${notCalc} not calculated`);
  return parts.join(', ');
}

const OUTCOME_TEXT = {
  cut: 'wins the group at these prices', already_wins: 'already wins these parts',
  blocked: 'cannot win these parts on price alone', no_qualifying_cut: 'no cut up to 90% both wins these parts and lowers your cost',
  incomplete: 'the search did not finish (not verified)', recommendation_out_of_date: 'the recommendation changed',
  invalid_group: 'these parts are not in the calculation', manual_wins: 'would win the group at your prices',
  manual_does_not_win: 'would not win the group at your prices',
};
const CAUSE_TEXT = { extra_supplier: 'your preference for fewer suppliers', moved_units: "your preference for today's supplier",
  risk: 'the risk costs you set', qualification: 'the qualification costs', together: 'these together' };

const PRICE_RE = /^\d+(\.\d{1,4})?$/;

function parseLines(text) {
  return text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => l.split(/[\t,]/).map((c) => c.trim()));
}

// readOnly (the scenario's Details, slice 2b): the facts and targets found, without the
// target and price tools (changes go through the chat).
function Recommendation({ api, eventId, res, names, currency, suppliers, definition, onChanged, readOnly, labelOf }) {
  const r = res.recommendation;
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState('');
  const money = (v) => formatAmount(v, currency);
  const ask = async (input) => {
    setMsg('');
    try {
      await api.scenarioAction(eventId, 'target', { result_id: res.result_id, ...input });
      setForm(null);
      onChanged();
    } catch (e) { setMsg(e.message); }
  };
  const e = r.preference_effect;
  return (
    <div className="srce-card srce-gap">
      <div className="srce-card-t">Why this answer</div>
      {r.explanation && <div>{r.explanation}</div>}
      {!r.explanation && r.explanation_status === 'pending' && <div className="srce-mut">Wingman is writing the explanation…</div>}
      {/* The facts are always shown (PLAN section 10); the paragraph, when there is one, sits above them. */}
      <ul className="srce-card-list">{r.facts.map((f) => (
        <li key={f.supplier}>{f.supplier}: {plural(f.lines, 'line', 'lines')} ({f.cheapest} at the lowest price allowed{f.held ? `, ${f.held} held by a rule or limit` : ''}{f.set_by_you ? `, ${f.set_by_you} set by you` : ''}{f.conditional ? `; ${f.conditional} need qualification` : ''}).</li>
      ))}
        {r.lines_kept_as_today > 0 && <li>{plural(r.lines_kept_as_today, 'line', 'lines')} kept as today.</li>}
      </ul>
      {/* Lane 1368 S5 (plan B3.7): the goal is shown, never interpreted. */}
      {/* Lane 1375 (Ryan R3): the rules at full size, and no "not checked" list. */}
      {r.goal_block && (
        <div className="srce-col srce-approach" data-testid="srce-rec-goal">
          <span><span className="srce-strong">Your goal: </span><span className="srce-goal-lines">{r.goal_block.goal}</span></span>
          {/* Lane 1393 (Ryan): what the award does, beside the goal, in plain figures. */}
          {r.goal_block.outcome && (
            <span><span className="srce-strong">This award: </span>{r.goal_block.outcome.direction === 'same'
              ? "the same as at today's prices on the offered parts."
              : `${r.goal_block.outcome.amount} ${r.goal_block.outcome.direction} than at today's prices on the offered parts (${r.goal_block.outcome.percent}%).`}</span>
          )}
          {r.goal_block.rules_from_goal.length > 0 && (
            <>
              <span className="srce-strong">Rules from your goal</span>
              <ul className="srce-approach-rules">{r.goal_block.rules_from_goal.map((text, i) => <li key={i}>{text}</li>)}</ul>
            </>
          )}
          {(r.goal_block.rules_in_place || []).length > 0 && (
            <>
              <span className="srce-strong">Already in place for your goal</span>
              <ul className="srce-approach-rules">{r.goal_block.rules_in_place.map((text, i) => <li key={i}>{text}</li>)}</ul>
            </>
          )}
        </div>
      )}
      {/* Lane 1393 (Ryan): only rules that changed the answer; no "none of your rules" line. */}
      {r.rule_effect && r.rule_effect.changed.length > 0 && (
        <div className="srce-col">
          <span>Rules that raised the cost:</span>
          {r.rule_effect.changed.map((id) => {
            const rule = ((definition && definition.rules) || []).find((x) => x.rule_id === id);
            return <span key={id} className="srce-sub">{rule ? ruleText(rule, names, currency, labelOf) : 'A rule of this event'}</span>;
          })}
          {r.rule_effect.checked < r.rule_effect.total && (
            <span className="srce-sub">{r.rule_effect.checked} of {plural(r.rule_effect.total, 'rule was', 'rules were')} checked (the check has a time limit).</span>
          )}
        </div>
      )}
      {e && e.known && e.changed && (
        <div>Your preferences and costs change the answer: the payable cost is {money(e.payable)}, {money(e.extra_cost)} more than without them ({money(e.payable_without)}), because of {(e.causes || []).map((c) => CAUSE_TEXT[c] || c).join(' and ')}{e.each_alone ? ' (each one alone gives this answer)' : ''}.</div>
      )}
      {e && e.known === false && <div className="srce-sub">Wingman could not finish checking whether your preferences change the answer (the check has a time limit).</div>}
      {/* Lane 1395 QA (Ryan): the read-only Details shows no target prices (the
          Negotiation tab holds targets). */}
      {!readOnly && (<>
      <div className="srce-card-t">Prices that would improve it</div>
      {r.targets.length === 0 && <div className="srce-mut">{r.targets_pending ? 'Working out target prices…' : 'No target prices yet.'}</div>}
      {r.targets.map((t) => (
        <div key={t.target_id} className="srce-col">
          <span><span className="srce-strong">{t.supplier_name}</span> on {t.parts.length} {t.parts.length === 1 ? 'part' : 'parts'}: {OUTCOME_TEXT[t.outcome] || t.outcome}
            {t.outcome === 'cut' ? ` (a cut of ${t.cut_percent}%, lowering the payable cost by ${money(t.details.improvement)})` : ''}
            {t.outcome === 'blocked' && t.details.reason === 'min_order_above_volume' ? ` (its minimum order is above the volume on ${t.details.parts.join(', ')})` : ''}
            {t.outcome === 'blocked' && t.details.reason === 'spend_limit_rule' ? ' (a rule limits this supplier in dollars, so Wingman does not work out a target price; check your own prices instead)' : ''}
            {t.outcome === 'no_qualifying_cut' && t.details.forced_payable ? ` (at a 90% cut the payable cost would be ${money(t.details.forced_payable)})` : ''}
            {['manual_wins', 'manual_does_not_win'].includes(t.outcome) && t.details.payable
              ? ` (payable cost ${money(t.details.payable)}, ${Number(t.details.difference) === 0 ? 'the same as the recommendation'
                : `${money(t.details.difference)} ${t.details.lower ? 'lower' : 'higher'} than the recommendation`})` : ''}.
            {t.origin === 'automatic' ? <span className="srce-sub"> Found by Wingman.</span> : null}</span>
          {t.prices && (
            <span className="srce-sub">{Object.entries(t.prices).slice(0, 6).map(([p, v]) => `${p} at ${formatUnitPrice(v, currency)}`).join('; ')}{Object.keys(t.prices).length > 6 ? ' and more' : ''}.</span>
          )}
        </div>
      ))}
      {r.targets_pending > 0 && r.targets.length > 0 && <div className="srce-mut">{r.targets_pending} more being worked out…</div>}
      {r.targets_not_computed > 0 && <div className="srce-sub">{r.targets_not_computed} more candidates were not computed. Ask for any supplier and parts below.</div>}
      {msg && <div className="srce-mut">{msg}</div>}
      {res.status !== 'optimal' && <div className="srce-sub">Target prices need a recommendation proven to be the best answer. Recommend again when the calculation can finish.</div>}
      {res.status === 'optimal' && !res.current && <div className="srce-sub">The recommendation is out of date. Recommend again to find target prices.</div>}
      {!readOnly && !form && res.status === 'optimal' && res.current && (
        <span className="srce-row">
          <button type="button" className="srce-btn" onClick={() => setForm({ mode: 'target', supplier_id: '', text: '' })}>Find a target price</button>
          <button type="button" className="srce-btn" onClick={() => setForm({ mode: 'manual', supplier_id: '', text: '' })}>Check your own prices</button>
        </span>
      )}
      {form && (
        <div className="srce-card srce-card--quiet">
          <select className="srce-input" value={form.supplier_id} onChange={(ev) => setForm({ ...form, supplier_id: ev.target.value })}>
            <option value="">Choose a supplier…</option>
            {suppliers.map((s) => <option key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</option>)}
          </select>
          <label className="srce-col">{form.mode === 'manual' ? 'One part and price per line (for example "NG-123, 4.10")' : 'One part number per line'}
            <textarea className="srce-input srce-textarea" value={form.text} onChange={(ev) => setForm({ ...form, text: ev.target.value })} /></label>
          <span className="srce-row">
            <button type="button" className="srce-btn srce-btn--primary" disabled={!form.supplier_id || !form.text.trim()}
              onClick={() => {
                const rows = parseLines(form.text);
                const input = { supplier_id: Number(form.supplier_id), part_numbers: rows.map((x) => x[0]) };
                if (form.mode === 'manual') {
                  // One price per part, above zero, at most 4 decimals (the server checks it too).
                  const bad = rows.find((x) => x.length !== 2 || !PRICE_RE.test(x[1]) || Number(x[1]) <= 0);
                  const parts = rows.map((x) => x[0]);
                  if (bad) { setMsg(`Give one price for each part, like "NG-123, 4.10" (check "${bad.join(', ')}").`); return; }
                  if (new Set(parts).size !== parts.length) { setMsg('Each part only once, please.'); return; }
                  input.prices = Object.fromEntries(rows.map((x) => [x[0], x[1]]));
                }
                ask(input);
              }}>{form.mode === 'manual' ? 'Check' : 'Find it'}</button>
            <button type="button" className="ll-clr" onClick={() => setForm(null)}>Cancel</button>
          </span>
          <div className="srce-sub">A target is a what-if for you. Nothing is sent to the supplier.</div>
        </div>
      )}
      </>)}
    </div>
  );
}

// compact (slice 2b, the scenario's read-only Details): only the explanation of the
// answer (why this answer, the goal, conflicts); no status, "Status now" or award
// checklist (Lane 1395 QA, Ryan), no totals, no lines and no target or price tools.
export default function ScenarioResult({ api, eventId, resultId, names, currency, onCompare, suppliers, definition, compact, labelOf }) {
  const [res, setRes] = useState(null);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    api.scenarioResult(eventId, resultId, { page }).then(setRes).catch((e) => setError(e.message));
  }, [api, eventId, resultId, page, tick]);
  // While the explanation or targets are being worked out, look again every 3 seconds.
  const waiting = Boolean(res && res.recommendation && (res.recommendation.explanation_status === 'pending'
    || res.recommendation.targets_pending > 0));
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setTimeout(() => setTick((n) => n + 1), 3000);
    return () => clearTimeout(t);
  }, [waiting, res]);

  if (error) return <div className="srce-error" role="alert">{error}</div>;
  if (!res) return <div className="ll-loading">Loading the result…</div>;
  const t = res.totals || {};
  const money = (v) => formatAmount(v, currency);
  return (
    <div className="srce-col srce-gap">
      {/* Lane 1395 QA (Ryan: "it doesn't make sense, just get rid of it"): the Details
          show no status headline, "Status now" or award checklist; the award review
          checks all of it when the buyer awards. */}
      {(!compact || res.current === false) && (
        <div className="srce-row">
          {!compact && <span className="srce-strong">{RESULT_STATUS_TEXT[res.status] || res.status}</span>}
          {res.current === false && <span className="srce-warn"> Out of date: the event changed since this was calculated.</span>}
        </div>
      )}
      {!compact && ['optimal', 'feasible'].includes(res.status) && (
        <div className="srce-cov">
          <table className="srce-tbl"><tbody>
            <tr className="srce-tr"><td>Quoted cost (offers)</td><td className="srce-num">{money(t.quoted)}</td></tr>
            <tr className="srce-tr"><td>Lines kept as today (an estimate at today's price)</td><td className="srce-num">{money(t.kept_as_today)}</td></tr>
            <tr className="srce-tr"><td className="srce-strong">Projected cost</td><td className="srce-num srce-strong">{money(t.projected)}</td></tr>
            <tr className="srce-tr"><td>Savings against today's price (offered units only)</td>
              <td className="srce-num">{t.savings !== null && t.savings !== undefined ? money(t.savings)
                : `${money(t.savings_known)} on ${t.savings_known_lines} lines; ${t.savings_gap_lines} lines have no price today`}</td></tr>
            {t.qualification && Number(t.qualification) > 0 && (
              <tr className="srce-tr"><td>Qualification cost ({plural(t.qualification_items || 0, 'qualification', 'qualifications')}, as your rule counts it)</td><td className="srce-num">{money(t.qualification)}</td></tr>
            )}
            {t.penalty && Number(t.penalty) > 0 && (
              <tr className="srce-tr"><td>Preference cost (not money you pay)</td><td className="srce-num">{money(t.penalty)}</td></tr>
            )}
            {t.risk && Number(t.risk) > 0 && (
              <tr className="srce-tr"><td>Risk cost you set (not money you pay)</td><td className="srce-num">{money(t.risk)}</td></tr>
            )}
          </tbody></table>
          <div className="srce-sub">For {coverageLine(t)}.</div>
        </div>
      )}
      {res.recommendation && (
        <Recommendation readOnly={compact} api={api} eventId={eventId} res={res} names={names} currency={currency} labelOf={labelOf}
          suppliers={suppliers || []} definition={definition} onChanged={() => setTick((n) => n + 1)} />
      )}
      {res.status === 'infeasible' && res.conflicts && (
        <div className="srce-card">
          <div className="srce-card-t">Why the rules cannot all hold</div>
          {res.conflicts.kind === 'data' && res.conflicts.min_order_conflict.map((c) => (
            <div key={`${c.part}-${c.supplier_id}`}>{supplierName(names, c.supplier_id)}'s minimum order for {c.part} is more than the volume.</div>
          ))}
          {(res.conflicts.sets || []).map((set, i) => (
            <div key={i}>These cannot all hold together: {set.map((x) => (x.kind === 'rule' ? `a rule (${x.rule_id.slice(0, 8)})` : `your what-if ${x.index + 1}`)).join(' and ')}.</div>
          ))}
          {res.conflicts.kind === 'incomplete' && <div>The search reached its time limit; the diagnosis is incomplete.</div>}
          {res.conflicts.more_may_exist && <div>More conflicts may exist.</div>}
          <div className="srce-sub">Change or remove one of them in the scenario or on the Rules tab. Nothing is changed for you.</div>
        </div>
      )}
      {!compact && t.by_supplier && Object.keys(t.by_supplier).length > 0 && (
        <div className="srce-scroll"><table className="srce-tbl">
          <thead><tr className="srce-tr"><th className="srce-th">Supplier</th><th className="srce-th">Lines</th><th className="srce-th">Units</th><th className="srce-th">Quoted cost</th></tr></thead>
          <tbody>{Object.entries(t.by_supplier).map(([s, v]) => (
            <tr key={s} className="srce-tr"><td>{supplierName(names, s)}<HistoryMark supplierId={s} /></td><td className="srce-num">{v.lines}</td>
              <td className="srce-num">{formatUnits(v.units)}</td><td className="srce-num">{money(v.quoted)}</td></tr>
          ))}</tbody>
        </table></div>
      )}
      {!compact && res.lines && res.lines.items.length > 0 && (
        <div className="srce-scroll"><table className="srce-tbl">
          <thead><tr className="srce-tr"><th className="srce-th">Part</th><th className="srce-th">Site</th><th className="srce-th">Goes to</th>
            <th className="srce-th">Units</th><th className="srce-th">Price</th><th className="srce-th">Line cost</th><th className="srce-th">Why</th></tr></thead>
          <tbody>{res.lines.items.map((l, i) => (
            <tr key={`${l.line_id}-${i}`} className="srce-tr"><td>{l.part}</td><td>{l.site}</td>
              <td>{l.to === 'NC' ? 'Kept as today' : l.to ? supplierName(names, l.to) : 'No option'}</td>
              <td className="srce-num">{formatUnits(l.units)}</td><td className="srce-num">{l.price ? formatUnitPrice(l.price, currency) : 'Not available'}</td>
              <td className="srce-num">{money(l.cost)}</td><td>{WHY_TEXT[l.why] || ''}</td></tr>
          ))}</tbody>
        </table>
        <div className="srce-row">
          {page > 1 && <button type="button" className="ll-clr" onClick={() => setPage(page - 1)}>Previous</button>}
          <span className="srce-sub">Page {res.lines.page} of {res.lines.pages}</span>
          {page < res.lines.pages && <button type="button" className="ll-clr" onClick={() => setPage(page + 1)}>Next</button>}
        </div></div>
      )}
      {onCompare && <button type="button" className="ll-clr srce-card-link" onClick={onCompare}>Compare with another scenario</button>}
    </div>
  );
}
